import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Truck, Phone, MapPin } from "lucide-react";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });

  const load = async () => {
    try { setItems((await api.get("/suppliers")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", phone: "", address: "" }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, phone: s.phone || "", address: s.address || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama supplier wajib diisi");
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success("Supplier disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus supplier "${s.name}"?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Supplier dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <PageHeader title="Supplier" subtitle={`${items.length} pemasok`}>
        <Button data-testid="add-supplier-btn" onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2">
          <Plus className="h-4 w-4" /> Tambah Supplier
        </Button>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supplier-list">
          {items.map((s) => (
            <div key={s.id} data-testid={`supplier-card-${s.id}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-blue-500/15 text-blue-400 flex items-center justify-center">
                    <Truck className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold">{s.name}</div>
                    {s.phone && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {s.phone}</div>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-supplier-${s.id}`} onClick={() => openEdit(s)} className="h-8 w-8 rounded-lg bg-[#F7F9FB] hover:bg-slate-200 flex items-center justify-center"><Pencil className="h-4 w-4 text-slate-600" /></button>
                  <button data-testid={`delete-supplier-${s.id}`} onClick={() => remove(s)} className="h-8 w-8 rounded-lg bg-[#F7F9FB] hover:bg-red-500/20 flex items-center justify-center"><Trash2 className="h-4 w-4 text-red-600" /></button>
                </div>
              </div>
              {s.address && <div className="text-xs text-slate-500 flex items-start gap-1 mt-3"><MapPin className="h-3 w-3 mt-0.5 shrink-0" /> {s.address}</div>}
            </div>
          ))}
          {items.length === 0 && <p className="text-slate-500 col-span-full text-center py-10">Belum ada supplier</p>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-form-dialog" className="bg-[#F7F9FB] border-slate-300 text-slate-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Supplier" : "Tambah Supplier"}</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">Data pemasok untuk input pembelian barang.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Nama Supplier</Label>
              <Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#F7F9FB] border-slate-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Telepon</Label>
              <Input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#F7F9FB] border-slate-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Alamat</Label>
              <Input data-testid="supplier-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#F7F9FB] border-slate-300" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB]">Batal</Button>
            <Button data-testid="save-supplier-btn" onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
